import type { Profile, ProfileEdit } from '../../api/types.ts';

/** The fields a person edits, in the form's own terms. */
export type ProfileDraft = Required<ProfileEdit>;

export const TEXT_LIMITS = {
  name: 80,
  jobTitle: 80,
  company: 80,
  phone: 32,
  website: 200,
  location: 80,
  bio: 280,
} as const;

export function draftOf(profile: Profile): ProfileDraft {
  return {
    name: profile.name,
    jobTitle: profile.jobTitle,
    company: profile.company,
    phone: profile.phone,
    website: profile.website,
    location: profile.location,
    bio: profile.bio,
    birthday: profile.birthday,
    photo: profile.photo,
  };
}

/** Only what changed, trimmed, so a save never rewrites a field nobody touched. */
export function changesFrom(base: ProfileDraft, draft: ProfileDraft): ProfileEdit {
  const edit: ProfileEdit = {};
  for (const key of Object.keys(draft) as Array<keyof ProfileDraft>) {
    const value = typeof draft[key] === 'string' ? (draft[key] as string).trim() : draft[key];
    if (value !== base[key]) (edit as Record<string, unknown>)[key] = value;
  }
  return edit;
}

/** "northwind.example" is what people type; the profile keeps a link that opens. */
export function normaliseWebsite(value: string): string {
  const trimmed = value.trim();
  if (!trimmed || /^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export type ProfileErrors = Partial<Record<keyof ProfileDraft, string>>;

/** The same rules the server applies, so a mistake is shown where it was made. */
export function validate(draft: ProfileDraft, today: Date = new Date()): ProfileErrors {
  const errors: ProfileErrors = {};
  for (const [key, limit] of Object.entries(TEXT_LIMITS) as Array<[keyof typeof TEXT_LIMITS, number]>) {
    if (draft[key].trim().length > limit) errors[key] = `Keep it under ${limit} characters`;
  }
  if (draft.phone && !/^[+()\d\s.-]*$/.test(draft.phone.trim())) errors.phone = 'Use digits, spaces and + ( ) . - only';
  if (draft.website) {
    try {
      const url = new URL(draft.website.trim());
      if (url.protocol !== 'https:' && url.protocol !== 'http:') errors.website = 'Enter a web address, like example.com';
    } catch {
      errors.website = 'Enter a web address, like example.com';
    }
  }
  if (draft.birthday) {
    const date = new Date(`${draft.birthday}T00:00:00`);
    if (Number.isNaN(date.getTime())) errors.birthday = 'Enter a date';
    else if (date > today) errors.birthday = 'Your birthday cannot be in the future';
  }
  return errors;
}

/** "Head of Partnerships at Northwind Studio", or whichever half exists. */
export function roleLine(jobTitle: string, company: string): string {
  const title = jobTitle.trim();
  const org = company.trim();
  if (title && org) return `${title} at ${org}`;
  return title || org;
}
